import { Location, DOCUMENT, isPlatformBrowser, isPlatformServer } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Router } from "@angular/router";
import { BaseService } from "@core/services/base.service";
import { LoadingService } from "@core/services/loading.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { BehaviorSubject, fromEvent } from "rxjs";
import { debounceTime } from "rxjs/operators";

export interface CustomWindowInterface extends Window {}

@Injectable({
  providedIn: "root"
})
export class WindowRefService extends BaseService {
  private _isMobile = new BehaviorSubject<boolean>(false);

  constructor(
    public readonly loadingService: LoadingService,
    @Inject(DOCUMENT) private _doc: Document,
    public readonly utilsService: UtilsService,
    public readonly router: Router,
    private readonly location: Location,
    @Inject(PLATFORM_ID) public readonly platformId: Object
  ) {
    super(loadingService);

    if (isPlatformBrowser(this.platformId)) {
      fromEvent(this.nativeWindow, "resize")
        .pipe(debounceTime(300))
        .subscribe(() => {
          this.checkDevice(window.innerWidth);
        });

      this.checkDevice(this.nativeWindow.innerWidth);
    }
  }

  get nativeWindow(): CustomWindowInterface {
    return this._doc.defaultView;
  }

  getViewPortAspectRatio(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 1;
    }

    return window.innerWidth / window.innerHeight;
  }

  scroll(options: any) {
    if (!isPlatformBrowser(this.platformId) || typeof this.nativeWindow?.scroll === "undefined") {
      return;
    }

    if (!options) {
      options = { top: 0, left: 0, behavior: "auto" };
    }

    let overrideScrollBehavior = false;

    if (options.behavior === "auto" || options.behavior === undefined) {
      overrideScrollBehavior = true;
    }

    if (overrideScrollBehavior) {
      this.nativeWindow.document.documentElement.style.scrollBehavior = "auto";
    }

    this.nativeWindow.scroll(options);

    if (overrideScrollBehavior) {
      this.utilsService.delay(100).subscribe(() => {
        this.nativeWindow.document.documentElement.style.scrollBehavior = "";
      });
    }
  }

  scrollToElement(selector: string, options?: boolean | ScrollIntoViewOptions) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    let attempts = 0;

    const _doScroll = () => {
      const timeout = 100;

      if (attempts >= 5) {
        return;
      }

      const $element = this.nativeWindow.document.querySelector(selector);
      if (!!$element) {
        this.utilsService.delay(timeout).subscribe(() => {
          $element.scrollIntoView(options);
        });
      } else {
        attempts++;
        this.utilsService.delay(timeout).subscribe(() => {
          _doScroll();
        });
      }
    };

    if (options === null || options === undefined) {
      options = { behavior: "smooth", block: "start", inline: "nearest" };
    }

    _doScroll();
  }

  focusElement(selector: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    let attempts = 0;

    const _doFocus = () => {
      const timeout = 100;

      if (attempts >= 5) {
        return;
      }

      const $element = this.nativeWindow.document.querySelector(selector) as HTMLElement;
      if (!!$element) {
        this.utilsService.delay(timeout).subscribe(() => {
          $element.focus();
        });
      } else {
        attempts++;
        this.utilsService.delay(timeout).subscribe(() => {
          _doFocus();
        });
      }
    };

    _doFocus();
  }

  getCurrentUrl(): URL {
    return new URL(this.nativeWindow.document.URL);
  }

  locationAssign(url: string) {
    this.loadingService.setLoading(true);
    this.nativeWindow.location.assign(url);
  }

  routeTo404(fromUrl?: string) {
    if (!fromUrl) {
      fromUrl = this.getCurrentUrl().pathname;
    }

    void this.router.navigateByUrl("/404", { skipLocationChange: true }).then(() => {
      this.location.replaceState(fromUrl);
    });
  }

  checkDevice(width: number) {
    this._isMobile.next(width < 768);
  }

  pushState(data: any, url: string) {
    this._pushOrReplaceState("pushState", data, url);
  }

  replaceState(data: any, url: string) {
    this._pushOrReplaceState("replaceState", data, url);
  }

  changeBodyOverflow(value: "hidden" | "auto"): void {
    if (isPlatformBrowser(this.platformId)) {
      const _document = this.nativeWindow.document;
      if (_document) {
        _document.body.classList.toggle("overflow-hidden", value === "hidden");
      }
    }
  }

  async copyToClipboard(text: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    // Handle empty text
    if (!text) {
      console.warn("Cannot copy empty text to clipboard");
      return false;
    }

    try {
      // Try multiple approaches for maximum compatibility across devices

      // Approach 1: Modern Clipboard API (most browsers, but may require permissions)
      try {
        if (this.nativeWindow?.navigator?.clipboard) {
          await this.nativeWindow.navigator.clipboard.writeText(text);
          return true;
        }
      } catch (clipboardError) {
        console.warn("Clipboard API failed:", clipboardError);
        // Continue to fallbacks
      }

      // Approach 2: Legacy execCommand approach (more compatible with mobile)
      // Create a temporary input element for better mobile compatibility
      const tempInput = this.nativeWindow.document.createElement("input");
      tempInput.setAttribute("readonly", ""); // Prevent mobile keyboard from appearing
      tempInput.value = text;
      tempInput.style.position = "absolute";
      tempInput.style.left = "-9999px"; // Move outside the screen
      tempInput.style.fontSize = "12pt"; // Prevent zooming on iOS

      // Add to DOM
      this.nativeWindow.document.body.appendChild(tempInput);

      // Handle iOS devices specifically
      if (navigator.userAgent.match(/ipad|iphone/i)) {
        // iOS-specific handling
        const range = document.createRange();
        range.selectNodeContents(tempInput);
        const selection = this.nativeWindow.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        tempInput.setSelectionRange(0, 999999); // For iOS
      } else {
        // For all other devices
        tempInput.select();
        tempInput.setSelectionRange(0, 999999); // For mobile devices
      }

      try {
        const successful = this.nativeWindow.document.execCommand("copy");
        if (successful) {
          this.nativeWindow.document.body.removeChild(tempInput);
          return true;
        }
      } catch (execError) {
        console.warn("execCommand fallback failed:", execError);
        // Continue to next fallback
      } finally {
        // Clean up regardless of success
        if (tempInput.parentNode) {
          this.nativeWindow.document.body.removeChild(tempInput);
        }
      }

      // Approach 3: Try textarea as last resort (older mobile browsers)
      const textArea = this.nativeWindow.document.createElement("textarea");
      textArea.value = text;
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = "0";
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";

      this.nativeWindow.document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = this.nativeWindow.document.execCommand("copy");
        this.nativeWindow.document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        this.nativeWindow.document.body.removeChild(textArea);
        console.warn("All clipboard methods failed:", err);
        return false;
      }
    } catch (err) {
      console.warn("Copy failed with critical error:", err);
      return false;
    }
  }

  private _pushOrReplaceState(method: "pushState" | "replaceState", data: any, url: string) {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const _history = this.nativeWindow.history;
    const currentState = JSON.parse(JSON.stringify(_history.state));

    if (currentState && currentState.url === url && JSON.stringify(currentState.data) === JSON.stringify(data)) {
      return;
    }

    _history[method](data, "", url);
  }
}
